// =============================================================================
// io/parsers/broker.ts — Tradovate and Rithmic.
// -----------------------------------------------------------------------------
// PORTED FROM `tradecoach-saas/app/index.html` — semantics, not code (D3):
// `pairFillsIntoTrades`, `parseTradovate`, `parseTradovatePaired`,
// `extractRithmicCompletedOrders`, `parseRithmic`.
//
// ── 🚨 WHY THIS IS A SECOND FILLS WALKER, NOT A REUSE OF THE ATAS ONE ──
//
// The obvious move is to point Tradovate at `parseAtasFillsFromRows`, and it is
// wrong. The old client carries TWO walkers on purpose and they differ in more
// than column names:
//
//   `parseAtasFills*`      Instrument / Direction / Volume — position EPISODES,
//                          collapsing a whole scale-in-and-out into one trade
//                          with an `executions[]` timeline.
//   `pairFillsIntoTrades`  Contract / B/S / Filled Qty — per-fill FIFO, emitting
//                          one trade per closing fill, no timeline.
//
// The old client's own comment says the second matches "the granularity of
// Tradovate's Performance Report so cross-source dedupe works": a trader who
// imports both the fills export and the Performance Report must not get two
// differently-shaped copies of the same trading. Collapsing Tradovate fills into
// episodes would produce exactly that.
//
// ⚠ So these trades carry NO `executions[]`, and therefore no Peak Capture
// (D83). That is a property of the source, not an omission here.
//
// ── ⚠ A `Status` COLUMN IS A FILTER, AND ITS ABSENCE IS NOT ──
//
// Rithmic's Completed Orders section lists cancelled and rejected orders beside
// filled ones. A row whose status is present and does not say filled/completed/
// done is skipped. A row with NO status column at all is KEPT — Tradovate's fills
// export has none, and treating missing as "not filled" would import nothing.
// =============================================================================

import { consumePrior, type PriorPosition } from './carryIn';
import { dedupeFillsById, getCol, numCol, numColSigned, type Row } from './columns';
import {
  buildImportedTrade, fullContractSym,
  type CarriedInClosed, type ImportedTrade, type OpenPosition,
} from './sierra';
import { openPositionFingerprint } from './sierra';
import { findContract, type ContractSpec } from '../../domain/trade';
import { parseDateOnly, parseDelimited, parseLine, parseTimeOnly } from './delimited';

export interface BrokerResult {
  trades: ImportedTrade[];
  openPositions: OpenPosition[];
  /** Rows the walker could not use. Counted, never silent. */
  skipped: number;
  /** Positions carried in from an earlier import that this file closed. */
  carriedInClosed?: CarriedInClosed[];
}

/**
 * A paired (one-row-per-round-trip) parse.
 *
 * ⚠ `skipped` exists because it used to be hard-coded to `0` at the call site
 * while this function dropped rows in five places. Three rows in, one trade out,
 * "0 skipped" — the user is told nothing went wrong on an import that lost two
 * thirds of the file.
 */
export interface PairedResult {
  trades: ImportedTrade[];
  skipped: number;
}

/**
 * Time columns, in priority order.
 *
 * ⚠ `Fill Time` before `Order Time`: an order placed at 09:29 and filled at
 * 09:31 belongs to the fill, and ordering the FIFO queue by placement time
 * reverses pairs whenever a resting order fills after a later market order.
 */
const TIME_COLS = [
  'Fill Time', 'fillTime', 'timestamp', 'Timestamp', 'Time', 'Date',
  'Order Time', 'orderTime',
  'Create Time (EDT)', 'Update Time (EDT)', 'Create Time', 'Update Time',
] as const;

/**
 * Ids used to STAMP a trade's provenance — `openFillId` / `closeFillId`.
 *
 * 🚨 THIS LIST IS NOT A DE-DUPLICATION KEY, AND USING IT AS ONE DELETES REAL
 * CONTRACTS. It leads with ORDER-level ids, which is right for provenance
 * (an order id is the most useful thing to trace a trade back to) and
 * catastrophic for de-duplication, because ONE ORDER LEGITIMATELY PRODUCES
 * MANY FILLS.
 *
 * ⚠ This client previously used this very list as the dedupe key, and the cost
 * was measured: an order that filled 10 + 7 + 1 had two of its three fills
 * discarded as "duplicates", so 18 contracts bought closed as 10, the closing
 * sell of 18 had nothing left to close against and INVENTED an 8-lot short that
 * never existed, and the round trip's P&L came out $855 → $500.
 *
 * De-duplication uses the shared `dedupeFillsById`, which leads with per-FILL
 * ids. The old client keeps exactly these two lists for exactly these two jobs;
 * `platforms.ts` states the same rule for MotiveWave in its own words. **Keep
 * them separate.**
 */
const ID_COLS = [
  'Order ID', 'orderId', 'OrderID', 'Order Id', 'Fill ID', 'fillId',
  'Id', 'ID', 'Trade ID', 'tradeId', 'Order Number', 'orderNumber',
] as const;

interface Lot {
  side: 'long' | 'short';
  qty: number;
  entry: number;
  openTime: string;
  fee: number;
  fillId: string;
  contract: string;
  /** The contract WITH its month — see `OpenPosition.contractFull`. */
  contractFull: string | null;
  account: string;
  dpp: number;
}

/**
 * Per-fill FIFO pairing for broker fills exports (Tradovate, Rithmic).
 *
 * ⚠ Queued PER ACCOUNT AND SYMBOL. One queue across a multi-account export pairs
 * a buy in one account against a sell in another and invents both trades.
 */
export function pairFillsIntoTrades(
  rows: readonly Row[],
  catalogue: readonly ContractSpec[],
  today: string,
  /** Positions earlier imports left open. Empty = behaviour exactly as before. */
  prior: readonly PriorPosition[] = [],
): BrokerResult {
  const deduped = dedupeFillsById(rows);
  const carriedInClosed: CarriedInClosed[] = [];
  const priorRemaining = new Map<string, number>();

  // ⚠ Sorted by TIME before walking. The file's own order is the platform's, not
  // the market's — Rithmic's Completed Orders is newest-first — and a FIFO walk
  // over reversed fills pairs every exit against the wrong entry.
  const sorted = [...deduped].sort((a, b) => {
    const da = new Date(getCol(a, ...TIME_COLS) || 0).getTime();
    const db = new Date(getCol(b, ...TIME_COLS) || 0).getTime();
    return (Number.isNaN(da) ? 0 : da) - (Number.isNaN(db) ? 0 : db);
  });

  const queues = new Map<string, Lot[]>();
  const trades: ImportedTrade[] = [];
  let skipped = 0;

  for (const r of sorted) {
    let sym = String(getCol(r, 'Contract', 'contract', 'Symbol', 'symbol', 'Product', 'product') || '').trim();
    if (!sym) { skipped++; continue; }
    // `@` strips Tradovate's venue suffix; whitespace strips Rithmic's padding.
    sym = sym.replace(/@.*$/, '').replace(/\s+/g, '');

    const acct = String(getCol(r, 'Account', 'Account ID', 'accountSpec', 'accountId', 'Account Id', 'AccountID') || '');
    const dt = getCol(r, ...TIME_COLS);
    const bs = String(getCol(r, 'B/S', 'buyOrSell', 'Buy/Sell', 'Side', 'side', 'Action') || '').toLowerCase().trim();

    // ⚠ `Qty To Fill` covers Rithmic's Completed Orders, where Status=Filled
    // means the order filled IN FULL, so the requested quantity is the realised
    // one. On a partially-filled row it would overstate — which is why the status
    // filter below is not optional for that source.
    const qty = parseInt(
      String(getCol(r, 'Filled Qty', 'filledQty', 'fillQty', 'Qty To Fill', 'Quantity', 'qty', 'Qty', 'Volume') || '0')
        .replace(/,/g, ''),
      10,
    );
    const px = numCol(r, 'Avg Fill Price', 'avgFillPrice', 'Fill Price', 'fillPrice', 'Price', 'price');
    const fee = numCol(r, 'Commission', 'commission', 'Fees', 'fees');

    const status = String(getCol(r, 'Status', 'status', 'orderStatus', 'Order Status') || '').toLowerCase();
    // ⚠ Only when PRESENT. See the header — Tradovate has no status column.
    if (status && !status.includes('fill') && !status.includes('completed') && !status.includes('done')) {
      skipped++; continue;
    }
    if (!qty || !px) { skipped++; continue; }

    const isBuy = bs === 'b' || bs === 'buy' || bs.startsWith('b') || bs === 'long' || bs === '1';
    const newSide: 'long' | 'short' = isBuy ? 'long' : 'short';

    const c = findContract(sym, catalogue);
    const dpp = c?.dollarsPerPoint ?? 0;
    const contract = c?.symbol || sym.toUpperCase();
    const contractFull = fullContractSym(sym);
    const fillId = String(getCol(r, ...ID_COLS) || '');

    const key = `${acct}|${sym}`;
    let queue = queues.get(key);
    if (!queue) { queue = []; queues.set(key, queue); }

    let remainingQty = qty;
    let remainingFee = fee;

    while (remainingQty > 0 && queue.length > 0 && (queue[0] as Lot).side !== newSide) {
      const head = queue[0] as Lot;
      const closeQty = Math.min(head.qty, remainingQty);
      const points = head.side === 'long' ? (px - head.entry) : (head.entry - px);
      const grossPnl = points * dpp * closeQty;
      // ⚠ Fees are apportioned on BOTH legs by the share of each being closed.
      // Charging the whole opening commission to the first partial exit makes
      // that trade look worse and the rest free.
      const allocOpenFee = head.fee * (closeQty / head.qty);
      const allocCloseFee = fee * (closeQty / qty);
      const tradeFees = allocOpenFee + allocCloseFee;

      trades.push(buildImportedTrade({
        date: parseDateOnly(head.openTime, today),
        timeIn: parseTimeOnly(head.openTime),
        timeOut: parseTimeOnly(dt),
        contract, contractFull,
        side: head.side, qty: closeQty,
        entry: head.entry, exit: px,
        fees: tradeFees,
        netPnl: grossPnl - tradeFees,
        dpp,
        sourceAccount: acct || null,
        openFillId: head.fillId || null,
        closeFillId: fillId || null,
        maePoints: null, mfePoints: null, maeSecs: null, mfeSecs: null,
      }));

      head.qty -= closeQty;
      head.fee -= allocOpenFee;
      remainingQty -= closeQty;
      remainingFee -= allocCloseFee;
      if (head.qty === 0) queue.shift();
    }

    // ── 🚨 BEFORE OPENING, ASK WHAT WAS ALREADY HELD (#12) ──
    //
    // This format has no `OpenClose` column, so there is no signal in the file
    // at all that a fill closed something opened earlier — which is exactly why
    // the phantom here is undetectable and why all three self-checks stay
    // silent. The prior position is the only source of truth left.
    if (remainingQty > 0 && prior.length) {
      const legs = consumePrior(prior, priorRemaining, {
        account: acct || '', contract, contractFull, side: newSide,
        qty: remainingQty, time: dt,
      });
      for (const leg of legs) {
        const p = leg.position;
        const points = p.side === 'long' ? px - p.avgEntry : p.avgEntry - px;
        const grossPnl = points * dpp * leg.qty;
        const tradeFees = leg.openFee + fee * (leg.qty / qty);
        trades.push(buildImportedTrade({
          date: parseDateOnly(p.openTime, today),
          timeIn: parseTimeOnly(p.openTime),
          timeOut: parseTimeOnly(dt),
          contract, contractFull,
          side: p.side, qty: leg.qty,
          entry: p.avgEntry, exit: px,
          fees: tradeFees, netPnl: grossPnl - tradeFees, dpp,
          sourceAccount: acct || null,
          openFillId: '', closeFillId: fillId,
          maePoints: null, mfePoints: null, maeSecs: null, mfeSecs: null,
        }));
        carriedInClosed.push({
          time: dt, contract, account: acct || '',
          qty: leg.qty, openedAt: p.openTime, fingerprint: p.fingerprint,
        });
        remainingQty -= leg.qty;
        remainingFee -= fee * (leg.qty / qty);
      }
    }

    if (remainingQty > 0) {
      queue.push({
        side: newSide, qty: remainingQty, entry: px, openTime: dt,
        fee: remainingFee, fillId, contract, contractFull, account: acct, dpp,
      });
    }
  }

  return { trades, openPositions: collectOpen(queues), skipped, carriedInClosed };
}

/** Whatever never closed — surfaced, not dropped. */
function collectOpen(queues: Map<string, Lot[]>): OpenPosition[] {
  const out: OpenPosition[] = [];
  for (const queue of queues.values()) {
    if (!queue.length) continue;
    // ⚠ One position per SIDE per queue: a queue only ever holds one side at a
    // time (opposite fills close against it), so summing is safe here.
    const side = (queue[0] as Lot).side;
    const openQty = queue.reduce((n, l) => n + l.qty, 0);
    if (!openQty) continue;
    const avgEntry = queue.reduce((n, l) => n + l.entry * l.qty, 0) / openQty;
    const head = queue[0] as Lot;
    // ⚠ `''` not null — the fingerprint is a join of these fields, and a null
    // would stringify as "null" for one export and "" for another, giving the
    // same open position two identities across re-imports.
    const base = {
      account: head.account || '', contract: head.contract, side,
      openQty, avgEntry, openTime: head.openTime || '',
    };
    // ⚠ Carried BESIDE the fingerprint, never inside it — see
    // `openPositionFingerprint`, which is a shared suppression key.
    out.push({
      ...base, contractFull: head.contractFull ?? null,
      openFee: queue.reduce((n, l) => n + (l.fee || 0), 0),
      fingerprint: openPositionFingerprint(base),
    } as OpenPosition);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Tradovate
// ---------------------------------------------------------------------------

const norm = (k: string): string => k.toLowerCase().replace(/[\s_\-/#]/g, '');

/** Whether a header set is Tradovate's PAIRED Performance Report. */
export function isTradovatePaired(headers: readonly string[]): boolean {
  const h = new Set(headers.map(norm));
  return (h.has('buyprice') || h.has('buydate') || h.has('buytimestamp') || h.has('boughttimestamp'))
    && (h.has('sellprice') || h.has('selldate') || h.has('selltimestamp') || h.has('soldtimestamp'));
}

/**
 * Tradovate's Performance Report — one row per round trip, both legs columned.
 *
 * ⚠ SIDE IS INFERRED FROM WHICH TIMESTAMP CAME FIRST. There is no side column:
 * bought-then-sold is a long, sold-then-bought is a short. Defaulting to long
 * would mislabel every short in the file and invert its points.
 *
 * ⚠ `netAuthoritative` is deliberately NOT set, because Tradovate exports P&L as
 * an ABSOLUTE value — a losing trade arrives positive. `buildImportedTrade`'s
 * sign repair is exactly for this source and must be allowed to run.
 */
export function parseTradovatePaired(
  rows: readonly Row[],
  catalogue: readonly ContractSpec[],
  today: string,
): PairedResult {
  const trades: ImportedTrade[] = [];
  let skipped = 0;

  rows.forEach((r, idx) => {
    let sym = String(getCol(r, 'Contract', 'contract', 'Symbol', 'symbol', 'Product', 'product') || '').trim();
    if (!sym) { skipped++; return; }
    sym = sym.replace(/@.*$/, '').replace(/\s+/g, '');

    const buyDate = getCol(r, 'boughtTimestamp', 'Bought Timestamp', 'Buy Date', 'buyTimestamp', 'buyDate', 'Buy Time', 'buyTime');
    const sellDate = getCol(r, 'soldTimestamp', 'Sold Timestamp', 'Sell Date', 'sellTimestamp', 'sellDate', 'Sell Time', 'sellTime');
    const buyPrice = numCol(r, 'Buy Price', 'buyPrice');
    const sellPrice = numCol(r, 'Sell Price', 'sellPrice');
    // ⚠ THE DATE GUARD IS LEFT AS THE OLD CLIENT HAS IT — `&&`, so one
    // timestamp is enough. A row missing a timestamp still has both prices and
    // is a real trade; it just lands on the import date. Tightening this to `||`
    // was my first fix and it was WRONG: it dropped rows the shipping client
    // imports, trading a mis-dated trade for a missing one.
    if (!buyDate && !sellDate) { skipped++; return; }
    // 🚨 BUT BOTH PRICES, NOT EITHER — and this one IS the money.
    //
    // With `&&` a row carrying only a buy price survives, and `exit` then reads
    // the MISSING sell price as **0**. On a 2-lot MES that is a stored loss of
    // −$74,000 behind entirely plausible-looking columns. A price of zero is not
    // a price, and no round trip can be reconstructed without both ends.
    if (!buyPrice || !sellPrice) { skipped++; return; }

    const qty = parseInt(
      String(getCol(r, 'Qty', 'qty', 'Quantity', 'quantity', 'Volume') || '1').replace(/,/g, ''), 10,
    ) || 1;
    // ⚠ `numColSigned`, matching the old client. `numCol` STRIPS parentheses
    // without reading them as negative, and several brokers write losses as
    // `(123.45)`. The sign repair below usually rescues it — but only when the
    // contract resolves, so on an uncatalogued symbol a loss would be stored as
    // a win of the same size with nothing anywhere reporting a problem.
    const pnl = numColSigned(r, 'P/L', 'pnl', 'PnL', 'Net P/L', 'Profit', 'Realized P/L', 'realizedPnl');
    const fees = numCol(r, 'Commission', 'commission', 'Fees', 'fees');

    const buyTs = buyDate ? new Date(buyDate).getTime() : 0;
    const sellTs = sellDate ? new Date(sellDate).getTime() : 0;
    const side: 'long' | 'short' = (buyTs && sellTs)
      ? (buyTs <= sellTs ? 'long' : 'short')
      : (buyTs ? 'long' : 'short');

    const openTime = side === 'long' ? buyDate : sellDate;
    const closeTime = side === 'long' ? sellDate : buyDate;
    const entry = side === 'long' ? buyPrice : sellPrice;
    const exit = side === 'long' ? sellPrice : buyPrice;

    const c = findContract(sym, catalogue);
    const sourceAccount = String(getCol(r, 'Account', 'Account ID', 'accountSpec', 'accountId', 'Account Id', 'AccountID') || '');
    const realId = getCol(r, 'tradeId', 'Trade ID', 'Trade Id', 'fillId', 'Fill ID', 'orderId', 'Order ID', 'Id', 'ID');
    // ⚠ Synthesised when absent, and it INCLUDES THE ROW INDEX. Two identical
    // scalps in the same second are two trades; a content-only fingerprint would
    // collapse them and lose one.
    const closeFillId = realId ? String(realId) : `tradovate-rt:${idx}:${closeTime}:${exit}:${qty}`;

    trades.push(buildImportedTrade({
      date: parseDateOnly(openTime, today),
      timeIn: parseTimeOnly(openTime),
      timeOut: parseTimeOnly(closeTime),
      contract: c?.symbol || sym.toUpperCase(),
      contractFull: fullContractSym(sym),
      side, qty, entry, exit, fees,
      netPnl: pnl !== 0 ? pnl : null,
      dpp: c?.dollarsPerPoint ?? 0,
      sourceAccount: sourceAccount || null,
      openFillId: null, closeFillId,
      maePoints: null, mfePoints: null, maeSecs: null, mfeSecs: null,
    }));
  });

  // ⚠ The unresolved-contract drop is COUNTED, not silent. It is the last place
  // a row can disappear, and it is the likeliest one on a real file — an
  // instrument this client has no catalogue entry for.
  const kept = trades.filter((t) => t.contract);
  return { trades: kept, skipped: skipped + (trades.length - kept.length) };
}

/** Dispatch: paired Performance Report, or the fills walker. */
export function parseTradovate(
  rows: readonly Row[],
  catalogue: readonly ContractSpec[],
  today: string,
): BrokerResult {
  if (!rows.length) return { trades: [], openPositions: [], skipped: 0 };
  const headers = Object.keys(rows[0] as Record<string, string>);
  if (isTradovatePaired(headers)) {
    const p = parseTradovatePaired(rows, catalogue, today);
    return { trades: p.trades, openPositions: [], skipped: p.skipped };
  }
  return pairFillsIntoTrades(rows, catalogue, today);
}

// ---------------------------------------------------------------------------
// Rithmic
// ---------------------------------------------------------------------------

/**
 * Pull the `Completed Orders` section out of an R Trader export.
 *
 * ⚠ THE HEADER IS NOT LINE 1. The file is several titled sections — Working
 * Orders, Completed Orders — so a normal CSV read takes a title row as the
 * header and produces one column called "Working Orders". The section title is
 * found first, its header is the next non-blank line, and data runs until a
 * blank line or the next title.
 */
export function extractRithmicCompletedOrders(text: string, delim = ','): Row[] {
  const lines = text.split(/\r?\n/);
  const firstCell = (line: string): string =>
    String(parseLine(line, delim)[0] ?? '').trim().toLowerCase();

  let titleIdx = -1;
  for (let i = 0; i < lines.length; i++) {
    if (firstCell(lines[i] as string) === 'completed orders') { titleIdx = i; break; }
  }
  if (titleIdx < 0) return [];

  let headerIdx = -1;
  for (let i = titleIdx + 1; i < lines.length; i++) {
    if ((lines[i] ?? '').trim()) { headerIdx = i; break; }
  }
  if (headerIdx < 0) return [];

  const dataLines: string[] = [];
  for (let i = headerIdx + 1; i < lines.length; i++) {
    const raw = lines[i] ?? '';
    if (!raw.trim()) break;
    const fc = firstCell(raw);
    if (fc === 'working orders' || fc === 'completed orders') break;
    dataLines.push(raw);
  }
  if (!dataLines.length) return [];

  return parseDelimited([lines[headerIdx] as string, ...dataLines].join('\n'), delim);
}

/** Whether a whole file looks like a Rithmic R Trader orders export. */
export function looksLikeRithmic(text: string): boolean {
  const t = text.toLowerCase();
  return t.includes('completed orders')
    && (t.includes('qty to fill') || t.includes('avg fill price'))
    && (t.includes('buy/sell') || t.includes('order number'));
}

export function parseRithmic(
  text: string,
  catalogue: readonly ContractSpec[],
  today: string,
  delim = ',',
): BrokerResult {
  const rows = extractRithmicCompletedOrders(text, delim);
  if (!rows.length) return { trades: [], openPositions: [], skipped: 0 };
  return pairFillsIntoTrades(rows, catalogue, today);
}
